import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { categories } from '@/mocks/categories';

type NavItem = { key: string; to?: string; section?: string };

const navItems: NavItem[] = [
  { key: 'nav_home', to: '/' },
  { key: 'nav_guide', to: '/article/usfans-spreadsheet-guide' },
  { key: 'nav_shopping_guide', to: '/article/usfans-shopping-guide' },
  { key: 'nav_categories', section: 'categories' },
  { key: 'nav_faq', section: 'faq' },
  { key: 'nav_safety', section: 'safety' },
];

export default function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);

  const isHome = location.pathname === '/';
  const solid = !isHome || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSection = (id: string) => {
    setMenuOpen(false);
    if (isHome) {
      scrollToSection(id);
    } else {
      navigate('/');
      setTimeout(() => scrollToSection(id), 150);
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
        solid ? 'bg-white/95 border-b border-background-200/80' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-white">
            <i className="ri-grid-line text-lg" />
          </span>
          <span
            className={`font-heading text-lg font-semibold whitespace-nowrap ${
              solid ? 'text-foreground-900' : 'text-white'
            }`}
          >
            {t('brand')}
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) =>
            item.to ? (
              <Link
                key={item.key}
                to={item.to}
                className={`px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  solid ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
                }`}
              >
                {t(item.key)}
              </Link>
            ) : (
              <button
                key={item.key}
                type="button"
                onClick={() => handleSection(item.section!)}
                className={`px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  solid ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white'
                }`}
              >
                {t(item.key)}
              </button>
            )
          )}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher />
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="rounded-full bg-accent-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-600 whitespace-nowrap cursor-pointer"
          >
            {t('hero_cta_buy')}
          </a>
        </div>

        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center lg:hidden cursor-pointer"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menu"
        >
          <i className={`${solid ? 'text-foreground-900' : 'text-white'} text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {menuOpen && (
        <div className="max-h-[85vh] overflow-y-auto border-t border-background-200/70 bg-white px-4 py-4 lg:hidden">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) =>
              item.to ? (
                <Link
                  key={item.key}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
                >
                  {t(item.key)}
                </Link>
              ) : (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => handleSection(item.section!)}
                  className="rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
                >
                  {t(item.key)}
                </button>
              )
            )}
          </nav>

          <button
            type="button"
            onClick={() => setMobileCategoriesOpen((v) => !v)}
            className="mt-3 flex w-full items-center justify-between rounded-lg bg-background-100 px-3 py-2.5 text-sm font-semibold text-foreground-800 cursor-pointer"
          >
            {t('nav_categories')}
            <span className="w-4 h-4 flex items-center justify-center text-foreground-500">
              <i className={mobileCategoriesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
            </span>
          </button>

          {mobileCategoriesOpen && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={c.link}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 rounded-lg border border-background-200/70 bg-white px-2 py-2 text-xs font-semibold text-foreground-700 hover:bg-primary-50 cursor-pointer"
                >
                  <span className="h-7 w-7 shrink-0 overflow-hidden rounded-md">
                    <img
                      src={c.image}
                      alt={t(`category_${c.key}`)}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  {t(`category_${c.key}`)}
                </a>
              ))}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-4">
            <LanguageSwitcher />
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="rounded-full bg-accent-500 px-4 py-2 text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
            >
              {t('hero_cta_buy')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}